import React, { useState, useEffect } from 'react';
import {
  Row, Col, Card, Typography, Table, Modal, Button, Tooltip, message, DatePicker, Space, Divider, Badge
} from 'antd';
import {
  FileSearchOutlined, ExclamationCircleOutlined, ClockCircleOutlined,
  CopyOutlined, CheckCircleOutlined, DollarOutlined, CalendarOutlined
} from '@ant-design/icons';
import axiosInstance from '../../../axiosInstance';
import moment from 'moment';
import './Debits.css'; // Custom CSS file for additional styling

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const Debits = () => {
  const shopId = localStorage.getItem('shopId');

  const [loading, setLoading] = useState(false);
  const [overdueData, setOverdueData] = useState([]);
  const [upcomingData, setUpcomingData] = useState([]);
  const [processedData, setProcessedData] = useState([]);
  const [overdueSummary, setOverdueSummary] = useState({});
  const [upcomingSummary, setUpcomingSummary] = useState({});
  const [processedSummary, setProcessedSummary] = useState({});
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [modalTitle, setModalTitle] = useState("");
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [dateRange, setDateRange] = useState([null, null]);
  const [reminderLoadingId, setReminderLoadingId] = useState(null);

  useEffect(() => {
    if (shopId) {
      fetchInvoices();
    } else {
      message.error('Shop ID not found. Please log in again.');
    }
  }, [shopId]);

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const [overdueRes, upcomingRes, processedRes] = await Promise.all([
        axiosInstance.get(`/shop/${shopId}/unpaid-invoices`),
        axiosInstance.get(`/invoices/upcoming/${shopId}`),
        axiosInstance.get(`/invoices/processed/${shopId}`),
      ]);

      setOverdueData(overdueRes.data.overdue_invoices || []);
      setOverdueSummary(overdueRes.data.overdue_summary || {});

      setUpcomingData(upcomingRes.data.upcoming_invoices || []);
      setUpcomingSummary(upcomingRes.data.upcoming_summary || {});

      const paymentDetails = processedRes.data.payment_details || [];
      const processedWithDetails = processedRes.data.processed_invoices.map(invoice => {
        const matchingPayments = paymentDetails.filter(p => p.invoice_id === invoice.id);
        return {
          ...invoice,
          payment_details: matchingPayments
        };
      });

      setProcessedData(processedWithDetails);
      setProcessedSummary(processedRes.data.processed_summary || {});
    } catch (error) {
      message.error('Failed to retrieve invoices.');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const showModal = (title, invoices) => {
    setModalTitle(title);
    setSelectedInvoices(invoices);
    setFilteredInvoices(invoices);
    setModalVisible(true);
  };

  const handleViewInvoice = (invoiceId) => {
    window.location.href = `/sales/view/${invoiceId}`;
  };

  const handleCopyInvoiceNumber = (invoiceNumber) => {
    navigator.clipboard.writeText(invoiceNumber).then(() => {
      message.success('Invoice number copied to clipboard');
    }).catch(() => {
      message.error('Failed to copy invoice number');
    });
  };

  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
    if (dates && dates.length === 2) {
      const [startDate, endDate] = dates;
      const filtered = selectedInvoices.filter(invoice => {
        const invoiceDate = moment(invoice.created_at);
        return invoiceDate.isBetween(startDate, endDate, null, '[]');
      });
      setFilteredInvoices(filtered);
    } else {
      setFilteredInvoices(selectedInvoices);
    }
  };

  const renderPaymentDetails = (paymentDetails) => {
    if (!paymentDetails || paymentDetails.length === 0) {
      return <Text type="secondary">No payment records</Text>;
    }

    return paymentDetails.map((p, index) => (
      <div key={index} className="payment-detail-item">
        <Space size="small">
          <Text type="secondary">Previous:</Text>
          <Text strong>Tsh {parseFloat(p.previous_amount || 0).toFixed(2)}</Text>
          <Divider type="vertical" />
          <Text type="secondary">Paid:</Text>
          <Text strong>Tsh {parseFloat(p.paid_amount || 0).toFixed(2)}</Text>
          <Divider type="vertical" />
          <Text type="secondary">Remaining:</Text>
          <Text strong>Tsh {parseFloat(p.remaining_amount || 0).toFixed(2)}</Text>
        </Space>
      </div>
    ));
  };

  const sendReminder = async (invoiceNumber) => {
    try {
      setReminderLoadingId(invoiceNumber);
      await axiosInstance.post(`/invoices/send-reminder/${invoiceNumber}`);
      message.success(`Reminder sent for invoice ${invoiceNumber}`);
    } catch (error) {
      console.error(error);
      message.error(`Failed to send reminder for invoice ${invoiceNumber}`);
    } finally {
      setReminderLoadingId(null);
    }
  };

  const columns = [
    {
      title: 'Invoice No',
      dataIndex: 'invoice_number',
      key: 'invoice_number',
      render: (invoiceNumber) => (
        <Space>
          <Text strong>{invoiceNumber}</Text>
          <Tooltip title="Copy Invoice Number">
            <Button
              icon={<CopyOutlined />}
              type="text"
              size="small"
              onClick={() => handleCopyInvoiceNumber(invoiceNumber)}
            />
          </Tooltip>
        </Space>
      ),
    },
    {
      title: 'Created Date',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => (
        <Space>
          <CalendarOutlined />
          <Text>{moment(date).format('YYYY-MM-DD')}</Text>
        </Space>
      ),
    },
    {
      title: 'Total Amount',
      dataIndex: 'total',
      key: 'total',
      render: (amount) => (
        <Space>
          
          <Text strong type="success">
            Tsh {parseFloat(amount || 0).toFixed(2)}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Badge 
          status={status === 'paid' ? 'success' : 'warning'} 
          text={status.toUpperCase()} 
        />
      ),
    },
    {
      title: 'Remaining',
      dataIndex: 'remaining_amount',
      key: 'remaining_amount',
      render: (amount) => (
        <Text type={amount > 0 ? 'danger' : 'success'}>
          Tsh {parseFloat(amount || 0).toFixed(2)}
        </Text>
      ),
    },
    {
      title: 'Payment Details',
      key: 'payment_details',
      render: (_, record) => renderPaymentDetails(record.payment_details),
    },
    {
      title: 'Actions',
      key: 'action',
      render: (_, record) => (
        <Space size="small">
          <Button 
            size="small" 
            onClick={() => handleViewInvoice(record.id)}
            icon={<FileSearchOutlined />}
          >
            View
          </Button>

          {modalTitle === 'Overdue Invoices' && (
            <Button
              size="small"
              type="primary"
              danger
              loading={reminderLoadingId === record.invoice_number}
              onClick={() => sendReminder(record.invoice_number)}
            >
              Send Reminder
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div className="debits-container">
      <Card 
        title={<Title level={4} className="page-title">Invoice Management Dashboard</Title>}
        bordered={false}
        className="summary-card"
      >
        <Row gutter={[24, 24]} className="summary-row">
          {/* Overdue Card */}
          <Col xs={24} md={8}>
            <Card
              className="status-card overdue-card"
              hoverable
              onClick={() => showModal('Overdue Invoices', overdueData)}
            >
              <Space direction="vertical" size="middle" className="card-content">
                <div className="card-header">
                  <ExclamationCircleOutlined className="card-icon overdue-icon" />
                  <Title level={5} className="card-title">Overdue Invoices</Title>
                </div>
                <div className="card-stats">
                  <Text className="stat-label">Total Unpaid</Text>
                  <Title level={3} className="stat-value overdue-value">
                    Tsh {parseFloat(overdueSummary.total_unpaid || 0).toFixed(2)}
                  </Title>
                </div>
                <div className="card-footer">
                  <Badge 
                    count={overdueSummary.overdue_count || 0} 
                    className="badge-count overdue-badge" 
                  />
                  <Text className="footer-text">overdue invoices</Text>
                </div>
              </Space>
            </Card>
          </Col>

          {/* Upcoming Card */}
          <Col xs={24} md={8}>
            <Card
              className="status-card upcoming-card"
              hoverable
              onClick={() => showModal('Due Invoices', upcomingData)}
            >
              <Space direction="vertical" size="middle" className="card-content">
                <div className="card-header">
                  <ClockCircleOutlined className="card-icon upcoming-icon" />
                  <Title level={5} className="card-title">Due Invoices</Title>
                </div>
                <div className="card-stats">
                  <Text className="stat-label">Total Unpaid</Text>
                  <Title level={3} className="stat-value upcoming-value">
                    Tsh {parseFloat(upcomingSummary.total_unpaid || 0).toFixed(2)}
                  </Title>
                </div>
                <div className="card-footer">
                  <Badge 
                    count={upcomingSummary.upcoming_count || 0} 
                    className="badge-count upcoming-badge" 
                  />
                  <Text className="footer-text">due invoices</Text>
                </div>
              </Space>
            </Card>
          </Col>

          {/* Processed Card */}
          <Col xs={24} md={8}>
            <Card
              className="status-card processed-card"
              hoverable
              onClick={() => showModal('Processed Invoices', processedData)}
            >
              <Space direction="vertical" size="middle" className="card-content">
                <div className="card-header">
                  <CheckCircleOutlined className="card-icon processed-icon" />
                  <Title level={5} className="card-title">Processed Invoices</Title>
                </div>
                <div className="card-stats">
                  <Text className="stat-label">Total Paid</Text>
                  <Title level={3} className="stat-value processed-value">
                    Tsh {parseFloat(processedSummary.total_paid_amount || 0).toFixed(2)}
                  </Title>
                </div>
                <div className="card-footer">
                  <Badge 
                    count={processedSummary.processed_count || 0} 
                    className="badge-count processed-badge" 
                  />
                  <Text className="footer-text">processed invoices</Text>
                </div>
              </Space>
            </Card>
          </Col>
        </Row>
      </Card>

      {/* Invoice Details Modal */}
      <Modal
        className="invoices-modal"
        title={
          <Space>
            <FileSearchOutlined />
            <Text strong>{modalTitle}</Text>
          </Space>
        }
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        width={1200}
        footer={[
          <Button key="close" onClick={() => setModalVisible(false)}>Close</Button>
        ]}
      >
        <div className="modal-filter">
          <RangePicker 
            onChange={handleDateRangeChange} 
            style={{ width: '100%', maxWidth: '400px' }} 
          />
        </div>
        <Table
          className="invoices-table"
          loading={loading}
          dataSource={filteredInvoices}
          columns={columns}
          rowKey="id"
          pagination={{ 
            pageSize: 10, 
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} invoices`
          }}
          scroll={{ x: 'max-content' }}
        />
      </Modal>
    </div>
  );
};

export default Debits;